/**
 * /drill — Drill Hub (drill-modes-design-v2.1.md)
 *
 * Inverts the v2 layout: modes become the top-level IA (3-tab segmented
 * control) and skill cards become compact (one metric per card, the active
 * mode's metric). One ★ REC badge per tab.
 */

import { type ReactNode, useMemo, useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageHeader } from "../components/PageHeader.js";
import {
  buildAllItems,
  MASTERY_PRESETS,
  buildAllSkillStates,
  coachDrillPick,
  recommendedSkillForMode,
  type SkillState,
  type ExerciseSkillId,
} from "@music-app/learning-system";
import {
  useCardsStore,
  usePrefsStore,
  loadReviewsSince,
  loadAllListenCounts,
  loadAllListenTotalItems,
  loadAllListenCoverage,
  loadAllChallengeData,
} from "@music-app/persistence";
import type { ReviewSummary, ChallengeData } from "@music-app/learning-system";

// ─── constants ────────────────────────────────────────────────────────────────

const OXBLOOD = "#872932";
const INFO_BLUE = "#5A8FB5";
const INK = "#1A1813";
const TAB_LISTEN = "#4A7FA5";
const TAB_DRILL = "#5A5550";
const TAB_PRACTICE = "#3D7A5F";
const TAB_CHALLENGE = "#9E4D5A";
const PAPER = "#FAF6EE";
const PAPER_WARM = "#F1EBDF";
const SUCCESS = "#0F6E56";
const INK_FADED = "#6A645A";

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const LS_TAB_KEY = "drill-hub-tab";
const LS_SKILL_KEY = "drill-hub-skill";

type ExerciseMode = "listen" | "practice" | "review" | "challenge";
const ALL_MODES: ExerciseMode[] = ["listen", "practice", "review", "challenge"];

const MODE_COLOR: Record<ExerciseMode, string> = {
  listen:    TAB_LISTEN,
  practice:  TAB_DRILL,
  review:    TAB_PRACTICE,
  challenge: TAB_CHALLENGE,
};
const MODE_TITLE: Record<ExerciseMode, string> = {
  listen:    "Listen",
  practice:  "Practice",
  review:    "Review",
  challenge: "Challenge",
};
const MODE_COUNTS: Record<ExerciseMode, boolean> = {
  listen:    false,
  practice:  false,
  review:    true,
  challenge: true,
};
const MODE_DESC: Record<ExerciseMode, string> = {
  listen:    "Passive listening. No input required. No schedule changes.",
  practice:  "Repeat at your own pace. No schedule changes.",
  review:    "Updates your FSRS schedule.",
  challenge: "Timed accuracy run. Interleaved, no hints.",
};

// ─── skill metadata ────────────────────────────────────────────────────────

interface SkillMeta {
  id: ExerciseSkillId;
  route: string;
  trackLabel: string;
  title: string;
  subtitle: string;
  /** nodeId of the introductory MDX lesson to link, if one is authored. */
  lessonNodeId?: string;
}

const SKILL_META: SkillMeta[] = [
  { id: "scales",      route: "/exercise/scales",      trackLabel: "I · pitch",        title: "Scale Degree",      subtitle: "A cadence sets the key, one note plays — name the solfège syllable.", lessonNodeId: "diatonic-degrees-major" },
  { id: "intervals",   route: "/exercise/intervals",    trackLabel: "II · intervals",  title: "Interval",          subtitle: "A cadence plays, then two notes — name the distance.", lessonNodeId: "diatonic-intervals-major" },
  { id: "modes",       route: "/exercise/modes",        trackLabel: "III · modes",      title: "Mode",              subtitle: "Hear a melody in a modal context — name the mode.", lessonNodeId: "modes-major-family" },
  { id: "chords",      route: "/exercise/chords",       trackLabel: "IV · harmony",     title: "Chord Quality",     subtitle: "Hear a chord — arpeggio then block — name its quality.", lessonNodeId: "triad-quality" },
  { id: "inversions",  route: "/exercise/inversions",   trackLabel: "V · harmony",      title: "Chord Inversion",   subtitle: "The chord quality is given — which note is in the bass?", lessonNodeId: "triad-inversions" },
  { id: "cadences",    route: "/exercise/cadences",     trackLabel: "VI · harmony",     title: "Cadence",           subtitle: "A short progression ends — name the cadence type.", lessonNodeId: "cadence-recognition" },
  { id: "progressions",route: "/exercise/progressions", trackLabel: "VII · harmony",    title: "Progression",       subtitle: "Hear a chord progression — name the Roman numeral pattern.", lessonNodeId: "diatonic-progressions" },
  { id: "dictation",           route: "/exercise/dictation",            trackLabel: "VIII · dictation",  title: "Melodic Dictation",  subtitle: "A melody plays — reproduce it note by note.", lessonNodeId: "melodic-dictation-narrow" },
  { id: "rhythmic-dictation",  route: "/exercise/rhythmic-dictation",   trackLabel: "IX · pitch",        title: "Rhythmic Dictation", subtitle: "A rhythm plays in meter context — mark which beats have notes.", lessonNodeId: "rhythmic-dictation-simple" },
];

// ─── mix definitions ──────────────────────────────────────────────────────────

interface MixDef {
  id: string;
  title: string;
  challengeKey: string;
  subtitle: string;
  skills: ExerciseSkillId[];
}

const MIXES: MixDef[] = [
  { id: "harmony",     title: "Harmony pack",      challengeKey: "mix-harmony",     subtitle: "Chord · Inversion · Cadence",      skills: ["chords", "inversions", "cadences"] },
  { id: "modes-deep",  title: "Modes deep",        challengeKey: "mix-modes-deep",  subtitle: "Mode · Scale degree · Dictation",  skills: ["modes", "scales", "dictation"] },
  { id: "int-cad",     title: "Int + Cadence",     challengeKey: "mix-int-cad",     subtitle: "The intermediate proving ground",  skills: ["intervals", "cadences"] },
  { id: "everything",  title: "Everything",        challengeKey: "mix-everything",  subtitle: "Random pulls across all 8 drills", skills: ["scales","intervals","chords","inversions","cadences","modes","progressions","dictation"] },
];

// ─── page ─────────────────────────────────────────────────────────────────────

export function ExerciseHubPage(): ReactNode {
  const cards = useCardsStore((s) => s.cards);
  const masteryPreset = usePrefsStore((s) => s.masteryPreset);
  const placementCompleted = usePrefsStore((s) => s.placementCompleted);
  const prefsLoaded = usePrefsStore((s) => s.loaded);
  const unlockEverything = usePrefsStore((s) => s.unlockEverything);
  const thresholds = MASTERY_PRESETS[masteryPreset];
  const items = useMemo(() => buildAllItems(), []);
  const navigate = useNavigate();

  // TODO (P39): read ?track query param to highlight the matching skill card
  //   once track→skill mapping is defined (harmony maps to 4 skills — needs design input).

  // ── tab + highlight state ─────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<ExerciseMode>(() => {
    const h = window.location.hash.slice(1) as ExerciseMode;
    if (ALL_MODES.includes(h)) return h;
    const saved = localStorage.getItem(LS_TAB_KEY) as ExerciseMode | null;
    if (saved && ALL_MODES.includes(saved)) return saved;
    return "practice"; // overridden by coach effect once skills load
  });

  const [highlightedSkillId, setHighlightedSkillId] = useState<ExerciseSkillId | null>(() => {
    const saved = localStorage.getItem(LS_SKILL_KEY);
    return saved ? (saved as ExerciseSkillId) : null;
  });

  const coachDefaultApplied = useRef(false);

  const [bannerDismissed, setBannerDismissed] = useState(false);

  // ── async data ────────────────────────────────────────────────────────────
  const [reviews, setReviews] = useState<ReviewSummary[]>([]);
  useEffect(() => {
    const since = new Date(Date.now() - SEVEN_DAYS_MS).toISOString();
    loadReviewsSince(since)
      .then((recs) =>
        setReviews(recs.map((r) => ({ itemId: r.itemId, correct: r.correct, timestamp: r.timestamp }))),
      )
      .catch(() => {/* non-fatal */});
  }, []);

  const [listenCounts, setListenCounts] = useState<Partial<Record<ExerciseSkillId, number>>>({});
  const [listenTotalItems, setListenTotalItems] = useState<Record<string, number>>({});
  const [listenCoveredCounts, setListenCoveredCounts] = useState<Record<string, number>>({});
  const [challengeData, setChallengeData] = useState<Partial<Record<ExerciseSkillId, ChallengeData>>>({});
  const [mixChallengeData, setMixChallengeData] = useState<Record<string, ChallengeData>>({});

  useEffect(() => {
    Promise.all([loadAllListenCounts(), loadAllListenTotalItems(), loadAllListenCoverage(), loadAllChallengeData()])
      .then(([counts, totals, coverage, cdata]) => {
        const drillIds: ExerciseSkillId[] = ["scales","intervals","chords","inversions","cadences","modes","progressions","dictation"];
        setListenCounts(counts as Partial<Record<ExerciseSkillId, number>>);
        setListenTotalItems(totals);
        setListenCoveredCounts(Object.fromEntries(Object.entries(coverage).map(([k, v]) => [k, v.length])));
        setChallengeData(
          Object.fromEntries(
            drillIds.map((id) => [id, cdata[id] ?? { best: null, attempts: 0 }]),
          ) as Partial<Record<ExerciseSkillId, ChallengeData>>,
        );
        const mixData: Record<string, ChallengeData> = {};
        for (const mix of MIXES) {
          mixData[mix.id] = cdata[mix.challengeKey] ?? { best: null, attempts: 0 };
        }
        setMixChallengeData(mixData);
      })
      .catch(() => {/* non-fatal */});
  }, []);

  // ── derived state ─────────────────────────────────────────────────────────
  const skills = useMemo(() => {
    const all = buildAllSkillStates(items, cards, thresholds, reviews, listenCounts, challengeData, undefined, undefined, unlockEverything);
    // Guitar skills are always excluded from the main hub grid and coach picks —
    // they live in the dedicated guitar hub at /drill/guitar.
    return all.filter((s) => s.id !== "fret-intervals" && s.id !== "fret-triads" && s.id !== "fret-inversions");
  }, [items, cards, thresholds, reviews, listenCounts, challengeData, unlockEverything]);
  const skillMap = useMemo(
    () => Object.fromEntries(skills.map((s) => [s.id, s])) as Record<ExerciseSkillId, SkillState>,
    [skills],
  );

  const coach = useMemo(() => coachDrillPick(skills), [skills]);
  const hasActivity = useMemo(
    () => skills.some((s) => s.exercise.totalReps > 0 || s.listen.count > 0),
    [skills],
  );
  const recSkill = useMemo(
    () => recommendedSkillForMode(activeTab === "challenge" ? "challenge" : activeTab === "listen" ? "listen" : "practice", skills),
    [activeTab, skills],
  );
  const challengeAllLocked = useMemo(() => skills.every((s) => s.challenge.state === "locked"), [skills]);

  // Apply coach defaults once skills load, if no saved preference or hash.
  useEffect(() => {
    if (!prefsLoaded) return;
    if (coachDefaultApplied.current || skills.length === 0) return;
    coachDefaultApplied.current = true;

    if (!placementCompleted) {
      setActiveTab("listen");
      history.replaceState(null, "", "#listen");
      setHighlightedSkillId("scales");
      return;
    }

    const hasHash = ALL_MODES.includes(window.location.hash.slice(1) as ExerciseMode);
    const hasSavedTab = !!localStorage.getItem(LS_TAB_KEY);
    if (!hasHash && !hasSavedTab) {
      const defaultTab: ExerciseMode = coach ? coach.mode : "listen";
      setActiveTab(defaultTab);
      history.replaceState(null, "", `#${defaultTab}`);
      localStorage.setItem(LS_TAB_KEY, defaultTab);
    }

    const hasSavedSkill = !!localStorage.getItem(LS_SKILL_KEY);
    if (!hasSavedSkill && coach) {
      setHighlightedSkillId(coach.skillId);
    }
  }, [prefsLoaded, placementCompleted, coach, skills]);

  const handleTabClick = (tab: ExerciseMode) => {
    setActiveTab(tab);
    history.replaceState(null, "", `#${tab}`);
    localStorage.setItem(LS_TAB_KEY, tab);
  };

  const handleSkillSelect = (skillId: ExerciseSkillId) => {
    setHighlightedSkillId(skillId);
    localStorage.setItem(LS_SKILL_KEY, skillId);
  };

  const handleCoachStart = () => {
    if (!coach) return;
    const meta = SKILL_META.find((m) => m.id === coach.skillId);
    if (meta) {
      handleSkillSelect(coach.skillId);
      navigate(`${meta.route}?mode=${coach.mode}`);
    }
  };

  return (
    <div className="max-w-5xl mx-auto py-9 px-8 pb-16">
      <PageHeader
        eyebrow="ear training"
        title="Ear Training"
        subhead="Pick a mode, then a skill. Switch tabs to see the same skills at a different rung of the ladder."
      />

      {/* Coach strip */}
      <CoachStrip coach={coach} hasActivity={hasActivity} onStart={handleCoachStart} />

      {/* Mode tab strip */}
      <ModeTabStrip activeTab={activeTab} onTabClick={handleTabClick} />

      {/* Locked Challenge banner */}
      {activeTab === "challenge" && challengeAllLocked && (
        <ChallengeBanner dismissed={bannerDismissed} onDismiss={() => setBannerDismissed(true)} />
      )}

      {/* Compact skill grid — 4-col above md, 2-col below */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-10">
        {SKILL_META.map((meta) => {
          const skill = skillMap[meta.id];
          if (!skill) return null;
          return (
            <SkillCardCompact
              key={meta.id}
              meta={meta}
              skill={skill}
              activeTab={activeTab}
              isRec={recSkill?.id === meta.id}
              isHighlighted={highlightedSkillId === meta.id}
              onSelect={() => handleSkillSelect(meta.id)}
              listenTotalItems={listenTotalItems[meta.id] ?? 0}
              listenCoveredCount={listenCoveredCounts[meta.id] ?? 0}
            />
          );
        })}
      </div>

      {/* Interleaved mixes section */}
      <MixesStrip skills={skillMap} mixChallengeData={mixChallengeData} />

      {/* Advanced / chromatic topic drills */}
      <div className="mt-6">
        <AdvancedStrip mode={activeTab} />
      </div>

    </div>
  );
}

// ─── CoachStrip ───────────────────────────────────────────────────────────────

function CoachStrip({
  coach,
  hasActivity,
  onStart,
}: {
  coach: ReturnType<typeof coachDrillPick>;
  hasActivity: boolean;
  onStart: () => void;
}): ReactNode {
  if (!hasActivity || !coach) {
    return (
      <div className="flex items-stretch border border-ink/20 mb-6 bg-paper">
        <div
          className="flex items-center justify-center px-5 py-4 min-w-[100px]"
          style={{ backgroundColor: OXBLOOD }}
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white text-center leading-tight">
            Coach<br />picks
          </span>
        </div>
        <div className="flex items-center justify-between flex-1 px-5 py-4">
          <p className="font-serif text-base text-ink">
            Take a placement test to get personalised recommendations.
          </p>
          <Link
            to="/placement"
            className="bg-transparent border border-ink/25 px-[18px] py-3 font-sans text-sm font-medium text-ink hover:bg-ink hover:text-paper transition-colors ml-4 whitespace-nowrap"
          >
            Start →
          </Link>
        </div>
      </div>
    );
  }

  const meta = SKILL_META.find((m) => m.id === coach.skillId);
  const modeColor = MODE_COLOR[coach.mode];
  const modeName = MODE_TITLE[coach.mode];

  return (
    <div className="flex items-stretch border border-ink/20 mb-6 bg-paper">
      <div
        className="flex items-center justify-center px-5 py-4 min-w-[100px]"
        style={{ backgroundColor: OXBLOOD }}
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white text-center leading-tight">
          Coach<br />picks
        </span>
      </div>
      <div className="flex items-center justify-between flex-1 px-5 py-4 gap-4">
        <div className="min-w-0">
          <p className="font-serif text-base text-ink font-medium">
            {meta?.title} ·{" "}
            <span
              className="font-mono text-[10px] uppercase tracking-[0.18em]"
              style={{ color: modeColor }}
            >
              {modeName} mode
            </span>{" "}
            <span className="font-mono text-[10px] text-inkFaded">
              · {coach.estimatedMinutes} min
            </span>
          </p>
          <p className="font-serif italic text-sm text-inkSoft mt-0.5 line-clamp-1">
            {coach.reason}
          </p>
        </div>
        <button
          type="button"
          onClick={onStart}
          className="bg-transparent border border-ink/25 px-[18px] py-3 font-sans text-sm font-medium text-ink hover:bg-ink hover:text-paper transition-colors whitespace-nowrap"
        >
          Start →
        </button>
      </div>
    </div>
  );
}

// ─── ModeTabStrip ─────────────────────────────────────────────────────────────

function ModeTabStrip({
  activeTab,
  onTabClick,
}: {
  activeTab: ExerciseMode;
  onTabClick: (tab: ExerciseMode) => void;
}): ReactNode {
  const activeColor = MODE_COLOR[activeTab];
  return (
    <div className="mb-5">
      {/* Tab row — active tab has a colored 3 px bottom border that overwrites the shared grey line */}
      <div
        className="grid grid-cols-4 border-b border-ink/20"
        role="tablist"
        aria-label="Drill mode"
      >
        {ALL_MODES.map((tab) => {
          const isActive = activeTab === tab;
          const color = MODE_COLOR[tab];
          return (
            <button
              key={tab}
              role="tab"
              aria-selected={isActive}
              onClick={() => onTabClick(tab)}
              className={`relative px-3 py-3 -mb-px text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ink/30${isActive ? "" : " hover:bg-ink/5"}`}
              style={{
                backgroundColor: isActive ? PAPER_WARM : undefined,
                borderBottom: `3px solid ${isActive ? color : "transparent"}`,
              }}
            >
              <span
                className="font-serif text-[18px] font-medium leading-tight"
                style={{ color: isActive ? color : INK }}
              >
                {MODE_TITLE[tab]}
                {MODE_COUNTS[tab] && (
                  <span
                    className="font-mono text-[9px] ml-1 align-middle"
                    style={{ color: SUCCESS }}
                    aria-label="Counts toward Today"
                    title="Counts toward Today"
                  >
                    ●
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>

      {/* Description bar — left stripe echoes the active tab color */}
      <div
        className="bg-paperWarm px-4 py-2.5 mt-2"
        style={{ borderLeft: `4px solid ${activeColor}` }}
      >
        <p className="font-serif italic text-sm text-inkSoft">
          {MODE_DESC[activeTab]}
          {" · "}
          <span
            className="not-italic"
            style={{ color: MODE_COUNTS[activeTab] ? SUCCESS : INK_FADED }}
          >
            {MODE_COUNTS[activeTab] ? "Counts" : "Free"}
          </span>
        </p>
      </div>
    </div>
  );
}

// ─── ChallengeBanner ──────────────────────────────────────────────────────────

function ChallengeBanner({
  dismissed,
  onDismiss,
}: {
  dismissed: boolean;
  onDismiss: () => void;
}): ReactNode {
  if (dismissed) {
    return (
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-4">
        🔒 Challenge unlocks at 4 mastered items per skill
      </p>
    );
  }
  return (
    <div className="border border-ink/20 bg-paper px-4 py-3 mb-5 flex items-start justify-between gap-4">
      <div>
        <p className="font-serif text-sm text-ink">
          🔒 Challenge unlocks per skill at 4 mastered items.
        </p>
        <p className="font-serif italic text-xs text-inkSoft mt-0.5">
          Drill the basics first — you&apos;re not far.
        </p>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors mt-0.5 whitespace-nowrap flex-shrink-0"
        aria-label="Dismiss challenge unlock notice"
      >
        dismiss
      </button>
    </div>
  );
}

// ─── card content helpers ─────────────────────────────────────────────────────

interface CardContent {
  metric: string;
  metricSub: string;
  barFill: number;   // 0..1
  barColor: string;
}

function getCardContent(
  activeTab: ExerciseMode,
  skill: SkillState,
  listenCoveredCount: number,
): CardContent {
  switch (activeTab) {
    case "listen": {
      const total = skill.exercise.total;
      const barFill = total === 0 ? 0 : Math.min(listenCoveredCount / total, 1);
      return {
        metric: listenCoveredCount === 0 ? "—" : `${listenCoveredCount}/${total}`,
        metricSub: listenCoveredCount === 0 ? "never heard" : "items",
        barFill,
        barColor: INFO_BLUE,
      };
    }
    case "practice":
    case "review": {
      const { mastered, total, lapse7d } = skill.exercise;
      const lapsing = lapse7d >= 0.25;
      return {
        metric: `${mastered}/${total}`,
        metricSub: lapsing ? `${Math.round(lapse7d * 100)}% lapse` : "mastered",
        barFill: total === 0 ? 0 : mastered / total,
        barColor: lapsing ? OXBLOOD : SUCCESS,
      };
    }
    case "challenge": {
      const { state, best } = skill.challenge;
      if (state === "locked") {
        return { metric: "🔒", metricSub: "locked", barFill: 0, barColor: OXBLOOD };
      }
      if (best === null) {
        return { metric: "—", metricSub: "untried", barFill: 0, barColor: OXBLOOD };
      }
      return {
        metric: `${Math.round(best * 100)}%`,
        metricSub: "best",
        barFill: best,
        barColor: OXBLOOD,
      };
    }
  }
}

// ─── SkillCardCompact ─────────────────────────────────────────────────────────

function SkillCardCompact({
  meta,
  skill,
  activeTab,
  isRec,
  isHighlighted,
  onSelect,
  listenTotalItems: _listenTotalItems,
  listenCoveredCount,
}: {
  meta: SkillMeta;
  skill: SkillState;
  activeTab: ExerciseMode;
  isRec: boolean;
  isHighlighted: boolean;
  onSelect: () => void;
  listenTotalItems: number;
  listenCoveredCount: number;
}): ReactNode {
  const [hovered, setHovered] = useState(false);
  const isChallengeLocked = activeTab === "challenge" && skill.challenge.state === "locked";
  const { metric, metricSub, barFill, barColor } = getCardContent(activeTab, skill, listenCoveredCount);
  const modeColor = MODE_COLOR[activeTab];

  // All text flips to paper variants on hover
  const fgPrimary  = hovered ? PAPER : INK;
  const fgFaded    = hovered ? `${PAPER}88` : INK_FADED;
  const barTrack   = hovered ? "rgba(255,255,255,0.2)"  : "rgba(26,24,19,0.08)";
  const barFillCol = hovered ? "rgba(255,255,255,0.85)" : barColor;
  const subLabelColor = hovered
    ? `${PAPER}BB`
    : isChallengeLocked
      ? INK_FADED
      : (activeTab === "practice" || activeTab === "review") && skill.isLapsing
        ? OXBLOOD
        : INK_FADED;

  const cardContent = (
    <>
      {/* Eyebrow row */}
      <div className="flex items-center justify-between gap-1 mb-1.5">
        <span
          className="font-mono text-[10px] uppercase tracking-[0.18em] truncate leading-none transition-colors duration-150"
          style={{ color: fgFaded }}
        >
          {meta.trackLabel}
        </span>
        <div className="flex-shrink-0">
          {isRec ? (
            <span
              className="font-mono text-[9px] uppercase tracking-wider px-1 py-0.5 leading-none transition-colors duration-150"
              style={hovered
                ? { backgroundColor: PAPER, color: modeColor }
                : { backgroundColor: modeColor, color: PAPER }}
            >
              ★ REC
            </span>
          ) : (
            <span className="flex items-center gap-1">
              {skill.isNew && (
                <span className="font-mono text-[9px] uppercase tracking-wider text-white px-1 py-0.5 bg-success leading-none">
                  NEW
                </span>
              )}
              {skill.isLapsing && (
                <span
                  className="font-mono text-[9px] leading-none transition-colors duration-150"
                  style={{ color: hovered ? `${PAPER}CC` : OXBLOOD }}
                  aria-label="Lapsing"
                >
                  ⚠
                </span>
              )}
            </span>
          )}
        </div>
      </div>

      {/* Title */}
      <h2
        className="font-serif text-[20px] font-medium leading-snug mb-1 transition-colors duration-150"
        style={{ color: fgPrimary }}
      >
        {meta.title}
      </h2>
      <p
        className="font-serif italic text-[12px] leading-snug mb-3 transition-colors duration-150"
        style={{ color: fgFaded }}
      >
        {meta.subtitle}
      </p>

      {/* Progress bar */}
      <div
        className="w-full h-[4px] mb-3 transition-colors duration-150"
        style={{ backgroundColor: barTrack }}
        role="progressbar"
        aria-valuenow={Math.round(barFill * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full transition-colors duration-150"
          style={{
            width: `${Math.min(barFill, 1) * 100}%`,
            backgroundColor: barFillCol,
            opacity: isChallengeLocked ? 0.3 : 1,
          }}
        />
      </div>

      {/* Metric */}
      <p
        className="font-serif text-[17px] font-medium leading-none tabular-nums mb-1 transition-colors duration-150"
        style={{ color: isChallengeLocked ? INK_FADED : fgPrimary }}
      >
        {metric}
      </p>

      {/* Sub-label */}
      <p
        className="font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-150"
        style={{ color: subLabelColor }}
      >
        {metricSub}
      </p>
    </>
  );

  const lessonLink = meta.lessonNodeId ? (
    <div className="border-t border-ink/5 px-3 py-2">
      <Link
        to={`/learn/${meta.lessonNodeId}?from=/drill`}
        className="font-mono text-[9px] uppercase tracking-[0.18em] text-accent hover:text-ink transition-colors"
        onClick={(e) => e.stopPropagation()}
        tabIndex={0}
      >
        Read →
      </Link>
    </div>
  ) : null;

  // Locked challenge cards: non-clickable, no hover — but the Read → link stays live.
  if (isChallengeLocked) {
    return (
      <div className="flex flex-col border border-ink/20 bg-paper">
        <div className="p-3" style={{ opacity: 0.55, cursor: "not-allowed" }}>
          {cardContent}
        </div>
        {lessonLink}
      </div>
    );
  }

  const containerStyle: React.CSSProperties = hovered
    ? { border: `1px solid ${modeColor}`, backgroundColor: modeColor }
    : isHighlighted
      ? { border: `1px solid ${modeColor}`, backgroundColor: PAPER_WARM }
      : { border: "1px solid rgba(26,24,19,0.2)", backgroundColor: PAPER };

  return (
    <div className="flex flex-col transition-colors duration-150" style={containerStyle}>
      <Link
        to={`${meta.route}?mode=${activeTab}`}
        onClick={onSelect}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ink/30"
        aria-label={`${meta.title} — ${activeTab} mode`}
      >
        <div className="flex flex-col p-3 h-full">
          {cardContent}
        </div>
      </Link>
      {lessonLink}
    </div>
  );
}

// ─── AdvancedStrip ────────────────────────────────────────────────────────────

// Chromatic + advanced-harmony topics that have items + a lesson but no bespoke
// skill-drill screen; drilled through the generic `/exercise/topic/:nodeId`.
// Kept out of the primary grid so the core ladder stays 9 tiles.
const TOPIC_DRILLS: { nodeId: string; title: string; subtitle: string }[] = [
  { nodeId: "chromatic-degrees-major", title: "Chromatic Degrees", subtitle: "di · ri · fi · le · te" },
  { nodeId: "chromatic-intervals", title: "Chromatic Intervals", subtitle: "tritone · A2 · d7" },
  { nodeId: "slash-chords", title: "Slash Chords", subtitle: "G/B · F/C · C/E · F/G" },
  { nodeId: "secondary-dominant-recognition", title: "Secondary Dominants", subtitle: "V/ii · V/V · V/vi" },
  { nodeId: "modulation-recognition", title: "Modulation", subtitle: "relative · parallel · dominant" },
];

function AdvancedStrip({ mode }: { mode: ExerciseMode }): ReactNode {
  const topicMode = mode === "challenge" ? "practice" : mode;
  return (
    <div>
      <div className="border-t border-ink/20 pt-5 mb-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-0.5">
          Advanced · Chromatic
        </p>
        <p className="font-serif italic text-xs text-inkSoft">
          Focused topic drills — chromatic tones and advanced harmony
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {TOPIC_DRILLS.map((t) => (
          <Link
            key={t.nodeId}
            to={`/exercise/topic/${t.nodeId}?mode=${topicMode}`}
            className="block rounded-lg border border-ink/15 bg-paper hover:border-ink/40 transition-colors px-4 py-3"
          >
            <p className="font-serif text-sm text-ink leading-tight">{t.title}</p>
            <p className="font-mono text-[10px] text-inkFaded mt-1 tracking-wide">{t.subtitle}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ─── MixesStrip ───────────────────────────────────────────────────────────────

function MixesStrip({
  skills,
  mixChallengeData,
}: {
  skills: Record<ExerciseSkillId, SkillState>;
  mixChallengeData: Record<string, ChallengeData>;
}): ReactNode {
  return (
    <div>
      <div className="border-t border-ink/20 pt-5 mb-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-0.5">
          Mixes
        </p>
        <p className="font-serif italic text-xs text-inkSoft">
          Interleaved challenge sessions — unlocked when constituent drills are mastered
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {MIXES.map((mix) => {
          const unlocked = mix.skills.every((id) => skills[id]?.challenge.state === "open");
          const cdata = mixChallengeData[mix.id];
          return <MixCard key={mix.id} mix={mix} unlocked={unlocked} challengeData={cdata} />;
        })}
      </div>
    </div>
  );
}

function MixCard({
  mix,
  unlocked,
  challengeData,
}: {
  mix: MixDef;
  unlocked: boolean;
  challengeData?: ChallengeData;
}): ReactNode {
  let statusLabel: string;
  let statusColor: string;
  if (!unlocked) {
    statusLabel = "locked";
    statusColor = INK_FADED;
  } else if (challengeData?.best != null) {
    statusLabel = `best ${Math.round(challengeData.best * 100)}%`;
    statusColor = OXBLOOD;
  } else {
    statusLabel = "untried";
    statusColor = INK_FADED;
  }

  const borderColor = unlocked ? `rgba(135,41,50,0.35)` : "rgba(26,24,19,0.15)";

  const cardContent = (
    <>
      <p className="font-serif text-[15px] font-medium text-ink leading-snug mb-1">
        {mix.title}
      </p>
      <p className="font-serif italic text-[11px] text-inkSoft leading-snug mb-2 flex-1">
        {mix.subtitle}
      </p>
      <p className="font-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: statusColor }}>
        {statusLabel}
      </p>
    </>
  );

  if (!unlocked) {
    return (
      <div
        className="flex flex-col p-3"
        style={{ border: `1px solid ${borderColor}`, backgroundColor: PAPER, opacity: 0.6 }}
      >
        {cardContent}
      </div>
    );
  }

  return (
    <Link
      to={`/exercise/mix/${mix.id}?mode=challenge`}
      className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ink/30"
      aria-label={`${mix.title} — interleaved challenge mix`}
    >
      <div
        className="flex flex-col p-3 h-full hover:opacity-80 transition-opacity"
        style={{ border: `1px solid ${borderColor}`, backgroundColor: PAPER }}
      >
        {cardContent}
      </div>
    </Link>
  );
}

